import React, { useState, useEffect } from 'react';
import { useCheckDayliteAccess } from '../hooks/useCheckDayliteAccess';
// Import updateProfile from firebase/auth
import { createUserWithEmailAndPassword, sendEmailVerification, User, updateProfile } from 'firebase/auth';
import { auth, db } from '../firebase/firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebaseConfig';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { Link as RouterLink, useNavigate } from 'react-router';
import { DayliteContactSimple } from 'shared/types/dayliteModels';
import PrivacyPolicyTerms from './policy/PrivacyPolicyTerms';

// You may need to adjust these imports based on your UI components
import {
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  Modal,
  Paper,
  IconButton,
  Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Logo from 'src/assets/images/jarrell.png';

const SignupPage = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  // Store Daylite data for later use
  const [contactData, setContactData] = useState<DayliteContactSimple | null>(null); // Ensure this is used
  const [accountType, setAccountType] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  const { checkDayliteAccess, createAccessDocument } = useCheckDayliteAccess();
  const navigate = useNavigate();

  // Create SMS opt-in document in Firestore
  const createSmsOptInDocument = async (phoneNumber: string) => {
    try {
      const optInDocRef = doc(db, 'optIn', phoneNumber);
      await setDoc(optInDocRef, {
        enabled: true,
        createdAt: Timestamp.now()
      });
      console.log('Successfully created SMS opt-in document for:', phoneNumber);
    } catch (error) {
      console.error('Error creating SMS opt-in document:', error);
      // Don't throw error - this shouldn't fail the signup process
    }
  };

  // Phone number masking function
  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/\D/g, '');

    // Apply formatting based on length
    if (phoneNumber.length < 4) {
      return phoneNumber;
    } else if (phoneNumber.length < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setPhone(formattedPhone);
  };

  // Step 1: User inputs email only
  const handleEmailSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const emailCheckResult = await checkDayliteAccess({ email } as any);

      if (emailCheckResult.success && emailCheckResult.dayliteContact) {
        // Check for dayliteContact
        // Store the contact data and account type
        setContactData(emailCheckResult.dayliteContact); // Store the full contact data
        setAccountType(emailCheckResult.accountType || null);
        setStep(2); // Move to phone input step
      } else {
        setError(emailCheckResult.error || 'Email not authorized. Please contact support.');
      }
    } catch (err) {
      console.error('Email check error:', err);
      setError('There was a problem checking your email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check password strength
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }

    let score = 0;

    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 10) score += 1;

    // Complexity checks
    if (/[A-Z]/.test(password)) score += 1; // Has uppercase
    if (/[a-z]/.test(password)) score += 1; // Has lowercase
    if (/[0-9]/.test(password)) score += 1; // Has number
    if (/[^A-Za-z0-9]/.test(password)) score += 1; // Has special char

    setPasswordStrength(score);
  }, [password]);

  // Get password strength color and label
  const getPasswordStrengthInfo = () => {
    if (passwordStrength === 0) return { color: '#e0e0e0', label: '' };
    if (passwordStrength <= 2) return { color: '#f44336', label: 'Weak' };
    if (passwordStrength <= 4) return { color: '#ff9800', label: 'Medium' };
    return { color: '#4caf50', label: 'Strong' };
  };

  const { color, label } = getPasswordStrengthInfo();
  const isPasswordStrengthSufficient = passwordStrength > 2; // Requires at least medium strength

  // Step 2: User opts in or out of text alerts
  const handlePhoneSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // If phone is provided, validate it (should be 10 digits when unformatted)
    if (phone) {
      const phoneDigits = phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        setError('Please enter a valid 10-digit phone number or leave blank to opt-out');
        setLoading(false);
        return;
      }
    }

    // Phone validation passed (or skipped), move to password step
    setLoading(false);
    setStep(3);
  };

  // Step 3: User creates password
  const handlePasswordSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (!isPasswordStrengthSufficient) {
      setError('Please choose a stronger password');
      setLoading(false);
      return;
    }

    try {
      // Use a promise to track the entire signup process
      const signupPromise = (async () => {
        // Create user with email and password
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const newUser = userCredential.user;
        setUser(newUser);

        // Update Firebase user profile with name from Daylite contact data
        if (contactData?.first_name && contactData?.last_name) {
          await updateProfile(newUser, {
            displayName: `${contactData.first_name} ${contactData.last_name}`
          });
        }

        // Send verification email immediately after account creation
        await sendEmailVerification(newUser);

        // Create access document using the stored Daylite data
        // Ensure contactData is not null before calling createAccessDocument
        if (contactData && accountType && newUser) {
          await createAccessDocument(newUser, contactData, accountType);
        } else {
          // Handle the case where contactData might be missing, though it shouldn't happen if step 1 succeeded
          console.warn('Missing contactData or accountType when trying to create access document.');
          // Optionally throw an error or set an error state
        }

        // Update phone number in Daylite if it doesn't match or is missing
        if (contactData && contactData.self) {
          const contactId = contactData.self.split('/').pop(); // Extract ID from self URL
          const phoneDigits = phone.replace(/\D/g, '');
          const contactPhoneDigits = contactData.phone ? contactData.phone.replace(/\D/g, '') : '';

          // Update phone if it doesn't match or if no phone is on file
          if (!contactData.phone || phoneDigits !== contactPhoneDigits) {
            try {
              const addPrimaryPhoneToContactFn = httpsCallable(functions, 'addPrimaryPhoneToContact');
              await addPrimaryPhoneToContactFn({
                contactId,
                phoneNumber: phone,
                label: 'Mobile'
              });
              console.log('Successfully updated contact phone number in Daylite');
            } catch (phoneUpdateError) {
              // Don't fail the signup if phone update fails, just log it
              console.warn('Failed to update phone number in Daylite:', phoneUpdateError);
            }
          }
        }

        // Create SMS opt-in document if user provided a phone number
        if (phone && phone.replace(/\D/g, '').length === 10) {
          await createSmsOptInDocument(phone.replace(/\D/g, ''));
        }

        return { success: true, user: newUser };
      })();

      // Wait for the signup process to complete
      await signupPromise;

      setSuccess(true);
      setStep(4); // Move to final verification step
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Send email verification
  const handleSendVerification = async () => {
    setLoading(true);
    setError('');

    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        setSuccess(true);
      } else {
        setError('No user is currently signed in');
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(err.message || 'Failed to send verification email');
    } finally {
      setLoading(false);
    }
  };

  // Privacy Policy Modal Component
  const PrivacyPolicyModal = () => (
    <Modal open={privacyModalOpen} onClose={() => setPrivacyModalOpen(false)} aria-labelledby="privacy-policy-title">
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90vw',
          maxWidth: 800,
          maxHeight: '90vh',
          overflow: 'auto',
          outline: 'none'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setPrivacyModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Stack spacing={2}>
          <PrivacyPolicyTerms />
          <Box sx={{ px: 4, pb: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              color="primary"
              onClick={() => setPrivacyModalOpen(false)}
            >
              Close
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Modal>
  );

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', p: 3, textAlign: 'center' }}>
      <Box mb={3} display="flex" justifyContent="center">
        <Box component="img" src={Logo} alt="Jarrell Logo" sx={{ maxWidth: 140 }} />
      </Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Create an Account
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {step === 1 && (
        <Box component="form" onSubmit={handleEmailSubmit} noValidate sx={{ mt: 1 }}>
          <Divider sx={{ mb: 1 }} />

          <Typography variant="h6" gutterBottom>
            Step 1: Enter your email address
          </Typography>

          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />

          <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading || !email}>
            {loading ? <CircularProgress size={24} /> : 'Check Access'}
          </Button>
        </Box>
      )}

      {step === 2 && (
        <Box component="form" onSubmit={handlePhoneSubmit} noValidate sx={{ mt: 1 }}>
          <Divider sx={{ mb: 1 }} />

          <Typography variant="h6" gutterBottom>
            Step 2: Opt in or Opt out of text alerts
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            We'd like to send you text messages for important event information. To Opt-in, please enter your phone
            number below. To Opt-out, leave the number blank.
          </Alert>

          <TextField
            margin="normal"
            fullWidth
            id="phone"
            label="Phone Number (Optional)"
            name="phone"
            autoComplete="tel"
            autoFocus
            value={phone}
            onChange={handlePhoneChange}
            disabled={loading}
            placeholder="(555) 555-5555"
            inputProps={{
              maxLength: 14 // Limit to formatted length
            }}
          />

          {/* Phone number verification alerts - only show when phone is fully entered */}
          {phone.replace(/\D/g, '').length === 10 && contactData && (
            <>
              {contactData.phone && phone.replace(/\D/g, '') === contactData.phone.replace(/\D/g, '') ? (
                <Alert severity="success" sx={{ mt: 1, mb: 1 }}>
                  The number you provided matches what we have on file
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                  The number you provided doesn't match the primary number we have on file. On account verification we
                  will use the provided number {phone} for texts.
                </Alert>
              )}
            </>
          )}

          <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
            By opting in, you agree to receive SMS messages from Jarrell Entertainment about your events and account.
            Message and data rates may apply. Message frequency varies based on user activity. Reply STOP to
            unsubscribe.
            <br />
            <Typography
              variant="body2"
              component="button"
              type="button"
              onClick={() => setPrivacyModalOpen(true)}
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 'inherit'
              }}
            >
              View our Privacy Policy and Terms of Service.
            </Typography>
          </Alert>

          <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading}>
            {loading ? (
              <CircularProgress size={24} />
            ) : phone && phone.replace(/\D/g, '').length === 10 ? (
              'Continue with text messaging'
            ) : (
              'Continue without text messaging'
            )}
          </Button>

          <Button fullWidth variant="outlined" onClick={() => setStep(1)} sx={{ mb: 2 }} disabled={loading}>
            Back
          </Button>
        </Box>
      )}

      {step === 3 && (
        <Box component="form" onSubmit={handlePasswordSubmit} noValidate sx={{ mt: 1 }}>
          <Typography variant="h6" gutterBottom>
            Step 3: Create a password
          </Typography>

          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />

          {/* Password strength indicator */}
          {password && (
            <Box sx={{ mt: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption">Password strength:</Typography>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color }}>
                  {label}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(passwordStrength / 6) * 100}
                sx={{
                  height: 8,
                  borderRadius: 2,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: color
                  }
                }}
              />
            </Box>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading || !password || !confirmPassword || !isPasswordStrengthSufficient}
          >
            {loading ? <CircularProgress size={24} /> : 'Create Account'}
          </Button>

          <Button fullWidth variant="outlined" onClick={() => setStep(2)} sx={{ mb: 2 }} disabled={loading}>
            Back
          </Button>
        </Box>
      )}

      {step === 4 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="h6" gutterBottom>
            Step 4: Verify your email
          </Typography>

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Account created successfully! Please verify your email to continue.
            </Alert>
          )}

          <Typography>
            We've sent a verification email to {email}. Please verify your email to complete registration.{' '}
            <b>Please check your spam folder if you don't see it in your inbox.</b>
          </Typography>

          <Button
            fullWidth
            variant="contained"
            onClick={handleSendVerification}
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Resend Verification Email'}
          </Button>

          <Button fullWidth variant="outlined" onClick={() => navigate('/sign-in')} sx={{ mb: 2 }}>
            Go to Login
          </Button>
        </Box>
      )}

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal />
    </Box>
  );
};

export default SignupPage;
